import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "../styles/UserInfoCard.css";
import { useUser } from "./Context/UserContext";

/* ── inline icon set (stroke = currentColor so CSS controls the colour) ───── */
const I = {
    user: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
    ),
    company: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18" /><path d="M5 21V5a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v16" /><path d="M9 7h2M9 11h2M9 15h2" /></svg>
    ),
    mail: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>
    ),
    phone: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
    ),
    pin: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
    ),
    badge: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="9" cy="10" r="2" /><path d="M15 9h3M15 13h3M7 15.5c.7-1.2 3.3-1.2 4 0" /></svg>
    ),
};

const UserInfoCard = ({ user, onClose, roleOverride }) => {
    const navigate = useNavigate();
    const { logoutUser } = useUser();
    const [loggingOut, setLoggingOut] = useState(false);
    if (!user) return null;

    const isVendor = !user.roles || user.roles.length === 0;
    const isSecurityUser =
        user?.emailId === "mindteck.security@mindteck.com" ||
        user?.user?.emailId === "mindteck.security@mindteck.com";

    // Fancy sign-out: play the full-screen transition, THEN clear + redirect.
    const handleLogout = async () => {
        setLoggingOut(true);

        try {
            await axios.post(
                "/api/Login/logout",
                {
                    Email:
                        user?.EmailId ||
                        user?.emailId ||
                        user?.EmailID ||
                        user?.roles?.[0]?.emailId ||
                        user?.user?.EmailId ||
                        user?.user?.emailId ||
                        "unknown"
                }
            );

            console.log("Logout logged successfully");

        } catch (error) {
            console.log("Logout API error:", error);
        }

        window.setTimeout(() => {
            logoutUser();
            navigate("/");
        }, 1500);
    };

    const roleLabel = roleOverride
        ? roleOverride
        : isSecurityUser
            ? "Security"
            : isVendor
                ? user.roleName || "Vendor"
                : (user.roles?.length
                    ? user.roles.map(r => r.role).join(' / ')
                    : "Employee");

    // Fields + matching icons, by account type
    const fields = isSecurityUser
        ? [
            { icon: I.user, key: "contact", label: "Contact", value: "Security" },
            { icon: I.mail, key: "email", label: "Email", value: "mindteck.security@mindteck.com" },
        ]
        : isVendor
            ? [
                { icon: I.company, key: "company", label: "Company", value: user.NameOfCompany || user.nameOfCompany || "—" },
                { icon: I.user, key: "contact", label: "Contact", value: user.ContactPersonName || user.contactPersonName || "—" },
                { icon: I.mail, key: "email", label: "Email", value: user.EmailId || user.emailId || "—" },
                { icon: I.phone, key: "phone", label: "Phone", value: user.ContactNumber || user.contactNumber || "—" },
                { icon: I.pin, key: "address", label: "Address", value: user.Address || user.address || "—" },
            ]
            : [
                { icon: I.badge, key: "emp", label: "Emp ID", value: user.roles[0].empId || "—" },
                { icon: I.user, key: "username", label: "Username", value: user.roles[0].username || "—" },
                { icon: I.mail, key: "email", label: "Email", value: user.roles[0].emailId || "—" },
            ];

    const displayName = isSecurityUser
        ? "Security"
        : isVendor
            ? user.NameOfCompany || user.nameOfCompany || user.ContactPersonName || user.contactPersonName || "Vendor"
            : user.roles[0].username || roleLabel;

    return (
        <>
        <div className="user-card uc-card" role="dialog" aria-label="User information">
            <span className="uc-caret" aria-hidden="true" />
            {/* ── Header with avatar ── */}
            <div className="user-card-header">
                <div className="uc-avatar" aria-hidden="true">{I.user}</div>
                <div className="uc-id">
                    <span className="uc-name" title={displayName}>{displayName}</span>
                    <span className="uc-role">{roleLabel}</span>
                </div>
                <button className="close-btn" onClick={onClose} aria-label="Close">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                </button>
            </div>

            {/* ── Body ── */}
            <div className="user-card-body">
                {fields.map((f) => (
                    <div className={`info-row info-row--${f.key}`} key={f.key}>
                        <span className="info-icon">{f.icon}</span>
                        <div className="info-content">
                            <span className="info-label">{f.label}</span>
                            <span className="info-value" title={String(f.value)}>{f.value}</span>
                        </div>
                    </div>
                ))}
            </div>

            {/* ── Footer ── */}
            <div className="user-card-footer">
                <button className={`logout-btn ${loggingOut ? "is-loading" : ""}`} onClick={handleLogout} disabled={loggingOut}>
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
                    Log out
                </button>
            </div>
        </div>

        {/* ── Fancy sign-out transition ── */}
        {loggingOut && (
            <div className="logout-overlay">
                <div className="logout-stage">
                    <div className="logout-ring">
                        <span className="logout-ring-spin" />
                        <span className="logout-door">
                            <svg viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
                        </span>
                    </div>
                    <p className="logout-title">Signing you out</p>
                    <div className="logout-bar"><span /></div>
                </div>
            </div>
        )}
        </>
    );
};

export default UserInfoCard;
